import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useToast,
  Textarea,
  Grid,
  Spinner,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";

export default function EditOrderForm1() {
  const { orderId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  const [tailors, setTailors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFiles, setImageFiles] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    amount: "",
    paid: "",
    pending: "",
  });

  const [orderDetails, setOrderDetails] = useState([]);

  // Measurement fields by item type
   const getMeasurementFields = (itemType) => {
  itemType = itemType?.toLowerCase();
  if (["lehenga", "l/c"].includes(itemType)) {
    return ["length", "width", "hip", "etc"];
  } else if (itemType === "blouse") {
    return ["neck", "length", "chest", "width", "sleeve", "etc"];
  } else if (["odni", "saree", "shirt", "pant"].includes(itemType)) {
    return ["length", "width", "hip", "etc"];
  }
  return ["length", "width", "hip", "etc"];
};

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axiosInstance.get(`/api/orders/${orderId}`);
        const order = res.data.order;

        setFormData({
          name: order.customer.name,
          phone: order.customer.phone,
          email: order.customer.email,
          amount: order.payment.amount,
          paid: order.payment.paid,
          pending: order.payment.pending,
        });

        setOrderDetails(
          order.orderDetails.map((item) => ({
            ...item,
            deliveryDate: item.deliveryDate?.slice(0, 10) || "",
            tailor: item.tailor?._id || "",
          }))
        );
      } catch (err) {
        toast({ title: "Failed to fetch order", status: "error" });
      }
    };
    fetchOrder();
  }, [orderId, toast]);

  useEffect(() => {
    const fetchTailors = async () => {
      try {
        const res = await axiosInstance.get("/api/users/roles/list");
        setTailors(res.data);
      } catch (err) {
        toast({ title: "Failed to load tailors", status: "error" });
      }
    };
    fetchTailors();
  }, [toast]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOrderDetailChange = (index, field, value) => {
    const updated = [...orderDetails];
    updated[index][field] = value;
    setOrderDetails(updated);
  };

  const handleMeasurementChange = (index, field, value) => {
    const updated = [...orderDetails];
    if (!updated[index].measurements) {
      updated[index].measurements = {};
    }
    updated[index].measurements[field] = value;
    setOrderDetails(updated);
  };

  const handleImageChange = (index, file) => {
    setImageFiles((prev) => ({ ...prev, [index]: file }));
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const payload = new FormData();

      payload.append("name", formData.name);
      payload.append("phone", formData.phone);
      payload.append("email", formData.email);

      payload.append(
        "payment",
        JSON.stringify({
          amount: formData.amount,
          paid: formData.paid,
          pending: formData.pending,
        })
      );

      payload.append("orderDetails", JSON.stringify(orderDetails));

      orderDetails.forEach((_, index) => {
        if (imageFiles[index]) {
          payload.append(`files-${index}`, imageFiles[index]);
        }
      });

      await axiosInstance.put(`/api/orders/${orderId}`, payload);

      toast({ title: "Order updated successfully", status: "success" });
      navigate("/dashboard/order");
    } catch (err) {
      toast({ title: "Failed to update order", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4} maxW="1200px" mx="auto">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#E83733">
        Edit Order
      </Text>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mb={4}>
        {["name", "phone", "email", "amount", "paid", "pending"].map(
          (field) => (
            <FormControl key={field}>
              <FormLabel>{field[0].toUpperCase() + field.slice(1)}</FormLabel>
              <Input
                type="text"
                name={field}
                value={formData[field]}
                onChange={handleChange}
              />
            </FormControl>
          )
        )}
      </Grid>

      {orderDetails.map((detail, index) => (
        <Box key={index} p={4} borderWidth="1px" borderRadius="md" mt={6}>
          <Text fontWeight="bold" color="#E83733" mb={2}>
            Item #{index + 1}
          </Text>

          {detail.image && (
            <Box mb={3}>
              <Text fontWeight="medium" mb={1}>
                Current Image:
              </Text>
              <Image
                src={detail.image}
                alt={`Item ${index + 1}`}
                boxSize="150px"
                objectFit="cover"
                borderRadius="md"
                border="1px solid #ccc"
                cursor="pointer"
                onClick={() => {
                  setPreviewImage(detail.image);
                  onOpen();
                }}
              />
            </Box>
          )}

          <FormControl mb={4}>
            <FormLabel>Replace Image</FormLabel>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(index, e.target.files[0])}
            />
          </FormControl>

          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            {["item", "size", "color", "design", "thread", "lace"].map(
              (field) => (
                <FormControl key={field}>
                  <FormLabel>
                    {field[0].toUpperCase() + field.slice(1)}
                  </FormLabel>
                  <Input
                    value={detail[field] || ""}
                    onChange={(e) =>
                      handleOrderDetailChange(index, field, e.target.value)
                    }
                  />
                </FormControl>
              )
            )}

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={detail.description || ""}
                onChange={(e) =>
                  handleOrderDetailChange(index, "description", e.target.value)
                }
              />
            </FormControl>

            <FormControl>
              <FormLabel>Delivery Date</FormLabel>
              <Input
                type="date"
                value={detail.deliveryDate || ""}
                onChange={(e) =>
                  handleOrderDetailChange(index, "deliveryDate", e.target.value)
                }
              />
            </FormControl>

            <FormControl>
              <FormLabel>Master</FormLabel>
              <Select
                value={detail.tailor || ""}
                onChange={(e) =>
                  handleOrderDetailChange(index, "tailor", e.target.value)
                }
              >
                <option value="">Select Tailor</option>
                {tailors.map((tailor) => (
                  <option key={tailor._id} value={tailor._id}>
                    {tailor.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Text mt={4} fontWeight="medium" color="gray.600">
            Measurements
          </Text>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
            gap={4}
            mt={2}
          >
            {getMeasurementFields(detail.item).map((field) => (
              <FormControl key={field}>
                <FormLabel>{field}</FormLabel>
                <Input
                  value={detail.measurements?.[field.toLowerCase()] || ""}
                  onChange={(e) =>
                    handleMeasurementChange(
                      index,
                      field.toLowerCase(),
                      e.target.value
                    )
                  }
                />
              </FormControl>
            ))}
          </Grid>
        </Box>
      ))}

      <Button
        colorScheme="red"
        mt={6}
        w="full"
        onClick={handleUpdate}
        isDisabled={loading}
        leftIcon={loading && <Spinner size="sm" />}
      >
        {loading ? "Updating..." : "Update Order"}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={4}>
            <Image
              src={previewImage}
              alt="Preview"
              width="100%"
              maxHeight="80vh"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
